import React, { useEffect, useState, useRef, useCallback } from "react";
import useAxios from "../../../utils/useAxios";
import { ParticipantPickerPanel } from "../../../components/CustomModel/ParticipantPickerPanel";
import { useIsWideViewport } from "../../../hooks/useIsWideViewport";
import i18n, { t } from "i18next";
import FeatherIcon from "feather-icons-react";
import { getGroupRoundNumber, getOrdinalSuffix, getRoundTitle, groupCharCode } from "../../../constants";
import { renderParticipantRow } from "../../../components/renderParticipantRow";

const ScheduleSelectMatchModal = ({
                                      isOpen,
                                      onClose,
                                      onSuccess,
                                      tournamentId,
                                      stageId,
                                      tournamentGrade,
                                      roundData,
                                      unscheduledMatches,
                                      setUnscheduledMatches,
                                      nextUrl,
                                      setNextUrl,
                                      pickedCandidateMatch,
                                      onPickCandidateMatch
                                  }) => {
    const api = useAxios();
    const isWide = useIsWideViewport();

    const [query, setQuery] = useState("");
    // Separate from the unscheduledMatches-fetch effect below on purpose -
    // that effect's deps include unscheduledMatches itself (re-fires on
    // every "load more" page), so clearing the query there was wiping out
    // an in-progress search mid-session instead of just on open. This one
    // depends only on isOpen, so it only fires on the open transition.
    useEffect(() => {
        if (isOpen) setQuery("");
    }, [isOpen]);
    const [loading, setLoading] = useState(false);
    // A Set of in-flight match ids, not one global boolean - on wide
    // viewports the panel stays open across multiple picks (see onClose
    // below), so a second pick needs to be submittable while the first is
    // still in flight instead of the whole list locking up until it resolves.
    const [submittingIds, setSubmittingIds] = useState(() => new Set());
    const [activeStageKey, setActiveStageKey] = useState(null);
    const [formatStagesOrder, setFormatStagesOrder] = useState([]);

    const listRef = useRef(null);
    const scrollPosRef = useRef(0);
    const stickyHeaderRef = useRef(null);
    const [stickyOffset, setStickyOffset] = useState(0);

    // Логика скролла точно как ты просила (без изменения CSS)
    // Keeps scroll-margin-top on the group headers matched to the sticky
    // zone's real rendered height, so jumping to a bracket/group via the
    // anchor menu lands the header just below it instead of underneath it.
    // Also directly measures (rather than assumes a fixed px value) how much
    // clearance is needed: position:sticky's reserved flow space doesn't
    // reliably match its rendered height once it's "stuck", so a hardcoded
    // value over/undershoots depending on how many tab rows the header ends
    // up with.
    // On wide viewports the header renders via ParticipantPickerPanel's
    // `header` prop *inside* the docked panel's own sticky top zone
    // (.modal-drag-handle-zone), which wraps it in that zone's own top/
    // bottom padding - stickyHeaderRef only measures the header itself, not
    // the padding around it, so scrollMarginTop came up short there and an
    // anchor jump still landed partly hidden under the zone. Measuring the
    // zone (stickyHeaderRef's closest .modal-drag-handle-zone ancestor)
    // instead of the header covers that padding too. listEl's own
    // margin-top compensation (below) stays narrow-only: on wide the header
    // and the list are two separate boxes (the zone isn't stacked above
    // listEl in the same flow), where the list already sits flush under the
    // fixed zone on its own - comparing their positions there was comparing
    // two unrelated boxes and shoving the list down by whatever arbitrary
    // (often huge) gap fell out of that, the "big empty scroll at the top"
    // bug.
    useEffect(() => {
        if (!isOpen || !stickyHeaderRef.current || !listRef.current) return;
        const stickyEl = stickyHeaderRef.current;
        const listEl = listRef.current;
        const stickyBox = (isWide && stickyEl.closest(".modal-drag-handle-zone")) || stickyEl;

        const measure = () => {
            setStickyOffset(stickyBox.offsetHeight + 8);
            if (isWide) return;
            listEl.style.marginTop = "0px";
            const gap = stickyEl.getBoundingClientRect().bottom - listEl.getBoundingClientRect().top;
            listEl.style.marginTop = `${Math.max(0, gap) + 8}px`;
        };
        measure();

        const resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(stickyBox);
        return () => resizeObserver.disconnect();
    }, [isOpen, isWide]);

    // isWide skips the narrow-only margin-top compensation above, so reset
    // it here instead whenever the wide/docked panel is what's actually
    // rendering - otherwise a value computed while narrow (e.g. right
    // before a resize crossed the breakpoint) would keep pushing the list
    // down after switching to wide.
    useEffect(() => {
        if (!isWide) return;
        if (listRef.current) listRef.current.style.marginTop = "0px";
    }, [isWide]);

    useEffect(() => {
        if (!isOpen) return;

        let scrollContainer = null;
        let handleScroll = null;

        const timer = setTimeout(() => {
            if (!listRef.current) return;

            scrollContainer = listRef.current;
            while (scrollContainer && scrollContainer !== document.documentElement) {
                const style = window.getComputedStyle(scrollContainer);
                if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
                    break;
                }
                scrollContainer = scrollContainer.parentElement;
            }

            if (!scrollContainer || scrollContainer === document.documentElement) {
                scrollContainer = window;
            }

            if (scrollContainer === window) {
                window.scrollTo(0, scrollPosRef.current);
            } else {
                scrollContainer.scrollTop = scrollPosRef.current;
            }

            handleScroll = (e) => {
                scrollPosRef.current = scrollContainer === window ? window.scrollY : e.target.scrollTop;
            };
            scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

        }, 100);

        return () => {
            clearTimeout(timer);
            if (scrollContainer && handleScroll) {
                scrollContainer.removeEventListener('scroll', handleScroll);
            }
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        if (unscheduledMatches === null) {
            const fetchInitial = async () => {
                setLoading(true);
                try {
                    const res = await api.get(`/api/v1.0/tournaments/${tournamentId}/schedules/matches/?limit=50`);
                    setUnscheduledMatches(res.data.results || res.data || []);
                    setNextUrl(res.data.next ? res.data.next.replace(/^https?:\/\/[^\/]+/, '') : null);
                } catch (e) {
                    console.error(e);
                } finally {
                    setLoading(false);
                }
            };
            fetchInitial();
        }
    }, [isOpen, tournamentId, unscheduledMatches, setUnscheduledMatches, setNextUrl]);

    // GroupStage.order and DrawStage.draw_number are two independent
    // sequences on the backend (see TournamentDrawStagesSettings.js's own
    // comment on this) - a match's own `stage_order` field mixes both
    // formats' raw numbers with no shared "position among all stages",
    // which is why the tab row could show a draw stage before a group
    // stage even though the tournament's own stage settings ("Етап 1",
    // "Етап 2"...) put the group stage first. Reconstructing the same
    // merged-by-position list the settings screen uses (and saves) is the
    // only way to get the tab order/names to actually match Жеребкування.
    useEffect(() => {
        if (!isOpen || !tournamentId) return;
        let cancelled = false;
        Promise.allSettled([
            api.get(`/api/v1.0/tournaments/${tournamentId}/group-stage/`),
            api.get(`/api/v1.0/tournaments/${tournamentId}/draw-stage/`)
        ]).then(([groupsRes, drawsRes]) => {
            if (cancelled) return;
            const groupStages = groupsRes.status === "fulfilled" ? (groupsRes.value?.data?.results || []) : [];
            const drawStages = drawsRes.status === "fulfilled" ? (drawsRes.value?.data?.results || []) : [];
            const merged = [
                ...groupStages.map(s => ({ ...s, __format: "groups", __pos: s.order || 0 })),
                ...drawStages.map(s => ({ ...s, __format: "draws", __pos: s.draw_number || 0 }))
            ].sort((a, b) => a.__pos - b.__pos)
                // __pos collides across formats often (e.g. a group stage's
                // own order=1 and a draw stage's own draw_number=1 both mean
                // "first of their own kind", not "tied for the same overall
                // slot") - re-indexing after the sort gives every stage a
                // unique, already-correctly-ordered position (stable sort
                // keeps groups before draws on a tie) to key matches off of,
                // instead of a raw number two different matches could share.
                .map((s, idx) => ({ ...s, __order: idx }));
            setFormatStagesOrder(merged);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [isOpen, tournamentId, api]);

    const loadMore = async () => {
        if (!nextUrl || loading) return;
        setLoading(true);
        try {
            const res = await api.get(nextUrl);
            setUnscheduledMatches(prev => [...(prev || []), ...(res.data.results || res.data || [])]);
            setNextUrl(res.data.next ? res.data.next.replace(/^https?:\/\/[^\/]+/, '') : null);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    // The place/draw tab row below (groupSections) is built purely from
    // whatever matches have already been paginated in - a tournament with
    // more than one page's worth of unscheduled matches (limit=50) used to
    // only show tabs for the places its first page happened to cover,
    // silently missing every later place/draw until the user manually
    // scrolled the list far enough to trigger the next page via the
    // IntersectionObserver sentinel below. Chaining straight through every
    // page in the background as soon as the previous one lands - not
    // waiting for a scroll or an empty search (the only other loadMore
    // triggers) - means every tab exists as soon as its own matches have
    // arrived, without the user needing to know to scroll first.
    useEffect(() => {
        if (!isOpen || !nextUrl || loading) return;
        loadMore();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, nextUrl, loading]);

    // No specific cell targeted (either never had one, or the previous
    // target was just filled and cleared by the parent's onSuccess - see
    // TournamentSchedule.js) - a match click here can't post anywhere yet,
    // so it just "picks" it instead: highlighted in this list, "Drop here"
    // on every empty cell on screen, until a cell is tapped.
    const hasTarget = roundData?.roundId != null && roundData?.courtId != null;

    const handleSelect = async (match) => {
        if (!hasTarget) {
            onPickCandidateMatch?.(match);
            return;
        }

        if (submittingIds.has(match.id)) return;
        setSubmittingIds(prev => new Set(prev).add(match.id));
        try {
            const payload = {
                court: roundData.courtId,
                round: roundData.roundId,
                match: match.id
            };

            if (roundData.scheduleMatchId) {
                await api.patch(`/api/v1.0/tournaments/${tournamentId}/schedules/stages/${stageId}/match/${roundData.scheduleMatchId}/`, payload);
            } else {
                await api.post(`/api/v1.0/tournaments/${tournamentId}/schedules/stages/${stageId}/match/`, payload);
            }

            // Moved here (after the request actually succeeds) from before
            // it - removing the row immediately made it vanish from the
            // list in the same tick the tap registered, before the
            // isSubmitting dimming below ever got a chance to render, same
            // "looks like nothing happened" bug as GridSelectPlayerModal.js/
            // TournamentGroupsSelectPlayerModal.js used to have.
            setUnscheduledMatches(prev => prev ? prev.filter(m => m.id !== match.id) : prev);

            onSuccess?.();
            // ParticipantPickerPanel is a plain blocking modal on every
            // viewport now (reverted off its old wide-only docked/non-
            // blocking mode) - closing after a successful schedule is the
            // only feedback that anything happened at all, same as it
            // always was on narrow.
            onClose();
        } catch (e) {
            console.error(e);
        } finally {
            setSubmittingIds(prev => {
                const next = new Set(prev);
                next.delete(match.id);
                return next;
            });
        }
    };

    // Placement brackets are generating two duplicate matches per pairing -
    // same match_number/source matches, one tagged with an odd draw_number
    // and one with the even one right after it (e.g. "7th place" and "8th
    // place" are really one single match deciding both places at once).
    // That's a backend data bug, not a real second match - dropping the
    // even one here keeps the picker from offering the same match twice
    // until it's fixed at the source.
    const matches = (unscheduledMatches || []).filter(m => !(m.draw_number != null && m.draw_number % 2 === 0));

    // Умный поиск с учетом всех твоих кейсов
    const filteredMatches = matches.filter(m => {
        const q = query.trim().toLowerCase();
        if (!q) return true;

        const terms = q.split(/\s+/);
        let text = "";

        if (tournamentGrade === 3) {
            const t1 = m.team_1_name || `${t("Team")} ${m.team_1_number || ""}`;
            const t2 = m.team_2_name || `${t("Team")} ${m.team_2_number || ""}`;
            text += ` ${t1} ${t2} `;
        } else {
            const players = [m.player_1_name, m.player_2_name, m.player_3_name, m.player_4_name];
            players.forEach(p => {
                if (p) text += ` ${p.first_name || ""} ${p.last_name || ""} `;
            });
        }

        if (m.match_number) text += ` m${m.match_number} м${m.match_number} ${m.match_number} `;
        if (m.draw_number) text += ` ${m.draw_number} `;
        if (m.stage_name) text += ` ${m.stage_name} `;
        if (m.group_number) text += ` ${m.group_number} `;

        text = text.toLowerCase();

        return terms.every(term => text.includes(term));
    });

    // Matches each unscheduled match up to its real settings-defined stage
    // (see the formatStagesOrder fetch above) - by name first (custom stage
    // names are unique in practice), then for draws by the DrawStage's own
    // draw_number (matches 1:1 with a match's own draw_number - see below),
    // finally falling back to "the one stage of this match's own format"
    // when neither matched. Returns null before formatStagesOrder has
    // loaded, or if nothing matched.
    const getMatchedFormatStage = (m) => {
        if (formatStagesOrder.length === 0) return null;
        if (m.stage_name) {
            const byName = formatStagesOrder.find(s => s.name === m.stage_name);
            if (byName) return byName;
        }
        // A tournament can have several independent draw stages (e.g. a
        // "Основна сітка" main bracket plus a fully separate "Втішний
        // турнір" consolation bracket) that each start their own draw
        // numbering at 1 - matching on format alone would always resolve
        // to whichever draw stage happens to be first, mislabeling the
        // second stage's matches as bogus extra "2nd Place" tabs inside
        // the first stage instead of showing them under their own stage
        // tab. Each DrawStage's own draw_number (from the draw-stage fetch
        // above) is what actually ties a match to the right one.
        if (!m.group_number) {
            const byDrawNumber = formatStagesOrder.find(s => s.__format === "draws" && s.draw_number === m.draw_number);
            if (byDrawNumber) return byDrawNumber;
        }
        return formatStagesOrder.find(s => s.__format === (m.group_number ? "groups" : "draws")) || null;
    };
    const getStagePos = (m) => getMatchedFormatStage(m)?.__order ?? (m.stage_order ?? 0);

    // Group matches by their bracket/group so same-origin matches sit together
    // instead of interleaved in whatever order the API returned them in —
    // makes the picker scannable instead of one long undifferentiated list.
    const sortedMatches = [...filteredMatches].sort((a, b) => {
        const sa = getStagePos(a), sb = getStagePos(b);
        if (sa !== sb) return sa - sb;
        const ga = a.group_number ?? 0, gb = b.group_number ?? 0;
        if (ga !== gb) return ga - gb;
        const da = a.draw_number ?? 0, db = b.draw_number ?? 0;
        if (da !== db) return da - db;

        if (a.group_number) {
            // Round-robin groups don't carry a round_number - order by the
            // same computed round (getGroupRoundNumber) shown in the row
            // itself, so matches from round 1 sit together, then round 2,
            // etc., instead of raw pairing order (1v2, 1v3, 1v4, 2v3...).
            const gra = getGroupRoundNumber(a.total, a.match_order) ?? 0;
            const grb = getGroupRoundNumber(b.total, b.match_order) ?? 0;
            if (gra !== grb) return gra - grb;
        } else {
            // Round 1 first, then quarters, semis, final, etc. — round_number
            // already counts up from R1, so plain ascending order matches
            // how the tournament is actually played, instead of listing the
            // final first and working backwards to R1 last.
            const ra = a.round_number ?? 0, rb = b.round_number ?? 0;
            if (ra !== rb) return ra - rb;
        }
        return (a.match_order ?? 0) - (b.match_order ?? 0);
    });

    // A tournament can have several stages at once (e.g. one group stage plus
    // two separate bracket stages), each holding its own groups/draws. Stage
    // is picked via its own top-level tab row instead of being repeated on
    // every group/draw header underneath it. Keyed off the matched format
    // stage's own (format, id) when formatStagesOrder has loaded - falls
    // back to the old per-match heuristic only while it's still loading, so
    // group-stage matches with neither stage_name nor stage_order don't
    // render the literal string "Stage null" in that brief window.
    const getStageFallbackKey = (m) => m.group_number ? "groups" : "draws";
    const getStageKey = (m) => {
        const fs = getMatchedFormatStage(m);
        return fs ? `s_${fs.__format}_${fs.id}` : `s_${m.stage_name || m.stage_order || getStageFallbackKey(m)}`;
    };

    const getGroupKey = (m) => {
        const fs = getMatchedFormatStage(m);
        const stagePart = fs ? `${fs.__format}_${fs.id}` : (m.stage_name || m.stage_order || getStageFallbackKey(m));
        return m.group_number ? `g_${stagePart}_${m.group_number}` : `d_${stagePart}_${m.draw_number}`;
    };

    const getGroupLabel = (m) => m.group_number
        ? `${t("Group")} ${groupCharCode(m.group_number)}`
        : `${m.draw_number}${getOrdinalSuffix(m.draw_number)} ${t("Place")}`;

    // Same as getGroupLabel but without the "Group" word for the tab
    // buttons - just the letter (A/B/C...), so the pill stays short next to
    // the place tabs ("1st Place") it sits alongside.
    const getGroupTabLabel = (m) => m.group_number
        ? groupCharCode(m.group_number)
        : `${m.draw_number}${getOrdinalSuffix(m.draw_number)} ${t("Place")}`;

    // Top tab row: one per stage, in order. Only meaningful (and only
    // rendered) when there's more than one stage to switch between. Labels
    // use the stage's own settings-defined name (same one shown in
    // Жеребкування/Групи) when it has one, otherwise "Stage 1", "Stage 2"...
    // by left-to-right position - since sortedMatches is already ordered by
    // getStagePos, that position is the same one the settings screen shows.
    const stageSections = [];
    const seenStageKeys = new Set();
    sortedMatches.forEach(m => {
        const key = getStageKey(m);
        if (!seenStageKeys.has(key)) {
            seenStageKeys.add(key);
            stageSections.push({ key, label: getMatchedFormatStage(m)?.name || m.stage_name || `${t("Stage")} ${stageSections.length + 1}` });
        }
    });

    useEffect(() => {
        if (stageSections.length === 0) return;
        if (!stageSections.some(s => s.key === activeStageKey)) {
            setActiveStageKey(stageSections[0].key);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stageSections.map(s => s.key).join("|")]);

    const stageFilteredMatches = stageSections.length > 1 && activeStageKey
        ? sortedMatches.filter(m => getStageKey(m) === activeStageKey)
        : sortedMatches;

    // Same rule as PlayerStatistic.js's own hasMultipleDraws: a plain single-
    // elimination stage only ever has draw_number 1, so the round code alone
    // (F/1/2/...) already says enough - a second draw (3rd place playoff,
    // 5-8 bracket, ...) is what makes draw 1 need its own "1 PL" label too.
    const hasMultipleDraws = stageFilteredMatches.some(m => m.draw_number > 1);

    // Second tab row: one per group/draw within the active stage — a quick
    // jump menu into the (now much shorter, single-stage) list below.
    const groupSections = [];
    const seenGroupKeys = new Set();
    stageFilteredMatches.forEach(m => {
        const key = getGroupKey(m);
        if (!seenGroupKeys.has(key)) {
            seenGroupKeys.add(key);
            groupSections.push({ key, label: getGroupTabLabel(m) });
        }
    });

    const scrollToGroup = (key) => {
        document.getElementById(`schedule-group-${key}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const selectStage = (key) => {
        setActiveStageKey(key);
        const el = listRef.current;
        el?.closest(".modal-overlay")?.scrollTo({ top: 0 });
        el?.closest(".modal-content")?.scrollTo({ top: 0 });
    };

    // One table per bracket/group — rows in fixed columns instead of stacked
    // cards are much easier to scan for a specific match/participant.
    const groupedTables = [];
    {
        const byKey = new Map();
        stageFilteredMatches.forEach(m => {
            const key = getGroupKey(m);
            let group = byKey.get(key);
            if (!group) {
                group = { key, label: getGroupLabel(m), isGroupStage: !!m.group_number, matches: [] };
                byKey.set(key, group);
                groupedTables.push(group);
            }
            group.matches.push(m);
        });
    }

    useEffect(() => {
        if (query.trim() && filteredMatches.length === 0 && nextUrl && !loading) {
            loadMore();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, filteredMatches.length, nextUrl, loading]);

    const observer = useRef();
    const sentinelRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && nextUrl) {
                loadMore();
            }
        });

        if (node) observer.current.observe(node);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, nextUrl]);

    if (!isOpen) return null;

    // Passed to ParticipantPickerPanel's `header` prop (see that component)
    // so this title/search/tabs block lives in the docked panel's sticky top
    // zone next to the close X, instead of scrolling away with the match
    // list underneath it.
    const header = (
        <div className="modal-sticky-header" ref={stickyHeaderRef}>
            <h2><FeatherIcon icon="list" />{t("Choose match")}</h2>
            <div className="form-group" style={{ position: "relative" }}>
                <FeatherIcon icon="search" size={16} className="search-icon" />
                <input
                    type="text"
                    className="search"
                    placeholder={t("Search match by participant")}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    style={{ paddingLeft: "30px" }}
                />
            </div>
            {stageSections.length > 1 && (
                <nav className="tabs-container" style={{ marginTop: "10px" }}>
                    <ul className="tabs">
                        {stageSections.map(s => (
                            <li key={s.key} className={activeStageKey === s.key ? "active" : ""}>
                                <a
                                    href="#"
                                    className={`action-button ${activeStageKey === s.key ? "btn-green" : ""}`}
                                    onClick={(e) => { e.preventDefault(); selectStage(s.key); }}
                                >
                                    {s.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>
            )}
            {groupSections.length > 1 && (
                <nav className="tabs-container" style={{ marginTop: "3px" }}>
                    <ul className="tabs">
                        {groupSections.map(section => (
                            <li key={section.key}>
                                <a
                                    href={`#schedule-group-${section.key}`}
                                    className="action-button"
                                    onClick={(e) => { e.preventDefault(); scrollToGroup(section.key); }}
                                >
                                    {section.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>
            )}
        </div>
    );

    return (
        <ParticipantPickerPanel isOpen={isOpen} onClose={onClose} icon="list" centered={false} header={header}>
            <form className="modal-form">
                <div
                    className="schedule-select-tables"
                    ref={listRef}
                >
                    {groupedTables.map(group => (
                        <div
                            key={group.key}
                            id={`schedule-group-${group.key}`}
                            style={{ scrollMarginTop: stickyOffset }}
                        >
                            <label className="schedule-group-header">{group.label}</label>
                            <table className="schedule-select-table">
                                <tbody>
                                    {group.matches.map(m => {
                                        const p1 = tournamentGrade === 3
                                            ? renderParticipantRow(m.team_1_name || (m.team_1_number ? `${t("Team")} ${m.team_1_number}`.trim() : ""), null, m, 0, tournamentGrade, t, true)
                                            : tournamentGrade === 2
                                                ? renderParticipantRow(m.player_1_name, m.player_3_name, m, 0, tournamentGrade, t, true)
                                                : renderParticipantRow(m.player_1_name, null, m, 0, tournamentGrade, t, true);
                                        const p2 = tournamentGrade === 3
                                            ? renderParticipantRow(m.team_2_name || (m.team_2_number ? `${t("Team")} ${m.team_2_number}`.trim() : ""), null, m, 1, tournamentGrade, t, true)
                                            : tournamentGrade === 2
                                                ? renderParticipantRow(m.player_2_name, m.player_4_name, m, 1, tournamentGrade, t, true)
                                                : renderParticipantRow(m.player_2_name, null, m, 1, tournamentGrade, t, true);

                                        const groupRound = getGroupRoundNumber(m.total, m.match_order);
                                        // Same stacked "label on top, round below" style as
                                        // PlayerStatistic.js's own round column - bold group
                                        // letter line (matches the group's own section header
                                        // above, instead of a generic "Gr" every row repeats),
                                        // then the compact round code underneath.
                                        // The match number used to be its own column on the right
                                        // of the row - moved here instead, inline on the round line
                                        // itself (same "· #N" separator TournamentScheduleMatchesModal
                                        // uses) rather than a line of its own.
                                        const roundText = group.isGroupStage
                                            ? <><span className="black-name">{groupCharCode(m.group_number)}</span>{groupRound ? <><br/>{t("R")}{groupRound}</> : null}{m.match_number ? <> · #{m.match_number}</> : null}</>
                                            // show_last_order:false drops the round title's own
                                            // "-{match_order}" suffix - redundant now that the real
                                            // match number sits right next to it.
                                            : <>{(m.draw_number > 1 || (m.draw_number === 1 && hasMultipleDraws)) && <><span className="black-name">{`${m.draw_number}${getOrdinalSuffix(m.draw_number)} ${t("PL")}`}</span><br/></>}{getRoundTitle(m.round_number, m.total, t, i18n, m.match_order, false)}{m.match_number ? <> · #{m.match_number}</> : null}</>;

                                        const isThisPicked = pickedCandidateMatch && String(pickedCandidateMatch.id) === String(m.id);
                                        // Dimmed in place (not removed from the list) while this
                                        // exact match's own schedule request is in flight - same
                                        // fix as GridSelectPlayerModal.js/TournamentGroupsSelect
                                        // PlayerModal.js's own isSubmitting: a full-screen narrow
                                        // modal has nothing else on screen to show the tap
                                        // registered, so with no visible state change here it read
                                        // as "nothing happened" for however long the request took.
                                        const isSubmitting = submittingIds.has(m.id);
                                        // Desktop-only drag straight onto a schedule cell, mirroring
                                        // the grid's own cell-to-cell drag (ScheduleTable.js's
                                        // handleDragStart/handleDrop) - only when this row can't
                                        // already schedule itself with one click (hasTarget: a "+"
                                        // on one specific cell already opened this panel), same
                                        // condition handleSelect above uses to fall into the pick-
                                        // then-tap-a-cell flow instead of posting immediately.
                                        // Reuses that exact flow: dragstart "picks" this candidate
                                        // (onPickCandidateMatch, same as a click would with no
                                        // target) and tags the drag so ScheduleTable.js's onDrop
                                        // recognizes it and places the already-picked candidate
                                        // instead of trying to move an existing scheduled match.
                                        const draggableHere = !hasTarget && !isSubmitting;
                                        return (
                                            <tr
                                                key={m.id}
                                                onClick={() => handleSelect(m)}
                                                draggable={draggableHere}
                                                onDragStart={draggableHere ? (e) => {
                                                    e.dataTransfer.setData("scheduleCandidatePick", "1");
                                                    e.dataTransfer.effectAllowed = "copy";
                                                    onPickCandidateMatch?.(m);
                                                } : undefined}
                                                style={{
                                                    opacity: (isThisPicked || isSubmitting) ? 0.5 : 1,
                                                    cursor: isSubmitting ? "default" : "pointer",
                                                    pointerEvents: isSubmitting ? "none" : "auto"
                                                }}
                                            >
                                                <td className="schedule-round-name">{roundText}</td>
                                                <td className="schedule-participant schedule-participant-1">{p1}</td>
                                                <td className="schedule-vs">vs</td>
                                                <td className="schedule-participant schedule-participant-2">{p2}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ))}

                    {nextUrl && !loading && (
                        <div ref={sentinelRef} style={{ height: "1px", width: "100%" }} />
                    )}

                    {loading && (
                        <div style={{ textAlign: "center", padding: "10px", color: "var(--secondary-color)" }}>
                            {t("Loading...")}
                        </div>
                    )}
                </div>

            </form>
        </ParticipantPickerPanel>
    );
};

export default ScheduleSelectMatchModal;
